// src/views/hrms/attendence/RegularizationTable.jsx
import React from "react";
import { Check, X, Eye } from "lucide-react";

/* -------------------- status chip color (case-insensitive) -------------------- */
const chip = (statusRaw) => {
  const s = String(statusRaw || "").toLowerCase();
  return (
    {
      pending: "bg-amber-100 text-amber-700",
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-rose-100 text-rose-700",
    }[s] || "bg-gray-100 text-gray-700"
  );
};

/* -------------------- formatters -------------------- */
const IST = "Asia/Kolkata";

const fmtDay = (ymd) => {
  const x = new Date(`${ymd}T00:00:00+05:30`);
  if (isNaN(x)) return ymd || "-";
  return x.toLocaleDateString("en-GB", {
    timeZone: IST,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtT = (d) =>
  d
    ? new Date(d).toLocaleTimeString("en-IN", {
        timeZone: IST,
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

export const requestedText = (r) => {
  if (r.type === "punch") return fmtT(r.time);
  if (r.type === "working_hours") return `${fmtT(r.time)} – ${fmtT(r.endTime)}`;
  return "-";
};

/* -------------------- main table component -------------------- */
export default function RegularizationTable({
  rows = [],
  onApprove = () => {},
  onReject = () => {},
  onView = () => {},
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-white/70">
          <tr>
            <th className="px-4 py-3">Employee</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Requested</th>
            <th className="px-4 py-3">Reason</th>
            <th className="px-4 py-3 text-center">Status</th>
            <th className="px-4 py-3 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan="7"
                className="px-4 py-6 text-center text-gray-500 dark:text-white"
              >
                No requests found.
              </td>
            </tr>
          ) : (
            rows.map((r) => {
              const rowId = String(r.id || r._id || "");
              const statusRaw = String(r.status || "").toLowerCase();
              const isPending = statusRaw === "pending";

              return (
                <tr
                  key={rowId}
                  className="border-t border-gray-100 dark:border-white/10 "
                >
                  {/* Employee */}
                  <td className="px-4 py-3 dark:text-white">
                    <div className="font-medium text-navy-700 dark:text-white">
                      {r.employeeName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-white">
                      {r.employeeId}
                    </div>
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3 dark:text-white">{r.typeLabel}</td>

                  {/* Date */}
                  <td className="px-4 py-3 dark:text-white">
                    {fmtDay(r.workDay)}
                  </td>

                  {/* Requested time(s) */}
                  <td className="px-4 py-3 dark:text-white">
                    {requestedText(r)}
                  </td>

                  {/* Reason */}
                  <td
                    className="max-w-[260px] truncate px-4 py-3 dark:text-white"
                    title={r.reason || ""}
                  >
                    {r.reason ?? "-"}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-center dark:text-white">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${chip(
                        statusRaw
                      )}`}
                      title={statusRaw || "-"}
                    >
                      {statusRaw
                        ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1)
                        : "-"}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-3">
                      <button
                        className="rounded-lg border p-2 transition hover:bg-gray-100 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
                        onClick={() => onView(rowId)}
                        title="View details"
                        aria-label="View regularization details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      {isPending && (
                        <>
                          <button
                            className="rounded-lg border bg-gradient-to-br from-green-100 to-green-200 p-2
               transition hover:from-green-200 hover:to-green-300
               hover:text-green-700"
                            onClick={() => onApprove(rowId)}
                            title="Approve"
                            aria-label="Approve request"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            className="rounded-lg border bg-gradient-to-br from-red-100 to-red-200 p-2
               transition hover:from-red-200 hover:to-red-300
               hover:text-red-700"
                            onClick={() => onReject(rowId)}
                            title="Reject"
                            aria-label="Reject request"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
